<template>
  <div class="step-preview">
    <div class="step-preview__hint">
      Valide visualmente quais produtos casam com as regras e como a grade ficará
      no e-commerce/callcenter. Clique em um produto para ver sua matriz.
    </div>

    <div class="step-preview__actions">
      <CButton
        color="primary"
        @click="carregar"
        :disabled="!podeCarregar || loading"
      >
        <CIcon v-if="!loading" name="cil-magnifying-glass" size="sm" class="mr-1" />
        <fa-icon v-else icon="spinner" spin />
        Visualizar produtos
      </CButton>
      <small v-if="!podeCarregar" class="step-preview__hint-secondary">
        Configure pelo menos 1 regra e 1 campo nas abas anteriores.
      </small>
    </div>

    <div v-if="carregado" class="step-preview__resultado">
      <div v-if="excedeLimite" class="step-preview__alerta">
        <fa-icon icon="exclamation-triangle" class="mr-2" />
        <div>
          <strong>Grade muito grande</strong>
          <p class="m-0 mt-1">
            O maior cluster gerado teria
            <strong>{{ maximoCluster }} produtos</strong>
            (limite: {{ limitePermitido }}). Pré-visualização desabilitada e
            cadastro será bloqueado. Refine as regras de classificação ou
            escolha um agrupador mais restritivo.
          </p>
        </div>
      </div>

      <div v-if="!excedeLimite" class="step-preview__total">
        <strong>{{ total }}</strong>
        {{ total === 1 ? "produto principal casa" : "produtos principais casam" }}
        com as regras
        <span v-if="temMais" class="step-preview__limite">
          (mostrando {{ limiteAplicado }} primeiros)
        </span>
        <span v-if="maximoCluster" class="step-preview__limite">
          · maior cluster: {{ maximoCluster }} SKUs
        </span>
      </div>

      <div v-if="!excedeLimite && !produtos.length" class="step-preview__empty">
        <CIcon name="cil-ban" size="lg" class="empty-icon" />
        <p>Nenhum produto encontrado</p>
        <small>Revise as regras na aba anterior.</small>
      </div>

      <div v-else-if="!excedeLimite" class="step-preview__grid">
        <div class="step-preview__lista">
          <div class="step-preview__lista-titulo">Produtos que casam</div>
          <div
            v-for="produto in produtos"
            :key="produto.id"
            class="step-preview__item"
            :class="{ 'step-preview__item--ativo': selecionado === produto.id }"
            @click="selecionar(produto.id)"
          >
            <span class="step-preview__item-id">#{{ produto.id }}</span>
            <span class="step-preview__item-descricao">{{ produto.descComp }}</span>
          </div>
        </div>

        <div class="step-preview__grade">
          <div class="step-preview__grade-titulo">
            Como o cliente verá — produto <strong>#{{ selecionado }}</strong>
            <div class="step-preview__grade-sublabel">
              Modelo meramente ilustrativo, cada sistema terá seu próprio layout de exibição.
            </div>
          </div>

          <div v-if="!amostra" class="step-preview__empty step-preview__empty--small">
            Selecione um produto na lista.
          </div>

          <div v-else-if="!amostra.skus.length" class="step-preview__empty step-preview__empty--small">
            Este produto principal não tem SKUs filhos.
          </div>

          <div v-else class="step-preview__grade-corpo">
            <!-- Filtros (campos sem eixo) -->
            <div v-if="filtros.length" class="grade-filtros">
              <div
                v-for="filtro in filtros"
                :key="filtro.coluna"
                class="grade-filtro"
              >
                <label>{{ filtro.label }}</label>
                <select
                  class="form-control form-control-sm"
                  v-model="filtrosSelecionados[filtro.coluna]"
                >
                  <option :value="null">Todos</option>
                  <option v-for="valor in filtro.valores" :key="valor || '__sem_valor__'" :value="valor">
                    {{ labelValor(filtro.coluna, valor) }}
                  </option>
                </select>
              </div>
            </div>

            <!-- MATRIZ 2D (linha + coluna) -->
            <div v-if="modo === 'matriz2d'" class="grade-tabela-wrapper">
              <table class="grade-tabela">
                <thead>
                  <tr>
                    <th class="grade-cell grade-cell--corner">
                      <span class="eixo-rotulo eixo-rotulo--linha">{{ eixoLinha.label }}</span>
                      <span class="eixo-rotulo-sep">×</span>
                      <span class="eixo-rotulo eixo-rotulo--coluna">{{ eixoColuna.label }}</span>
                    </th>
                    <th
                      v-for="valorColuna in eixoColuna.valores"
                      :key="valorColuna || '__sem_valor__'"
                      class="grade-cell grade-cell--head-col"
                    >
                      {{ labelValor(eixoColuna.coluna, valorColuna) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="valorLinha in eixoLinha.valores" :key="valorLinha || '__sem_valor__'">
                    <th class="grade-cell grade-cell--head-row">
                      {{ labelValor(eixoLinha.coluna, valorLinha) }}
                    </th>
                    <td
                      v-for="valorColuna in eixoColuna.valores"
                      :key="valorColuna || '__sem_valor__'"
                      class="grade-cell grade-cell--multi"
                      :class="{ 'grade-cell--vazia': !skusPara({ [eixoLinha.coluna]: valorLinha, [eixoColuna.coluna]: valorColuna }).length }"
                    >
                      <template v-if="skusPara({ [eixoLinha.coluna]: valorLinha, [eixoColuna.coluna]: valorColuna }).length">
                        <div
                          v-for="sku in skusPara({ [eixoLinha.coluna]: valorLinha, [eixoColuna.coluna]: valorColuna })"
                          :key="sku.produto_id"
                          class="sku-card"
                          :title="sku.descComp"
                        >
                          <img
                            :src="cdnUrlSku(sku.produto_id)"
                            class="sku-card__imagem"
                            :alt="sku.descComp"
                            @error="$event.target.style.visibility = 'hidden'"
                          />
                          <div class="sku-card__id">#{{ sku.produto_id }}</div>
                          <div class="sku-card__descricao" :title="sku.descComp">{{ sku.descComp }}</div>
                          <input class="sku-card__quantidade" type="number" min="0" placeholder="0" />
                        </div>
                      </template>
                      <span v-else class="grade-cell__vazio">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- MATRIZ 1D — só coluna (1 linha horizontal) -->
            <div v-else-if="modo === 'so-coluna'" class="grade-tabela-wrapper">
              <table class="grade-tabela">
                <thead>
                  <tr>
                    <th class="grade-cell grade-cell--head-row">
                      <span class="eixo-rotulo eixo-rotulo--coluna">{{ eixoColuna.label }}</span>
                    </th>
                    <th
                      v-for="grupo in gruposCabecalhoColuna"
                      :key="grupo.chave"
                      :colspan="grupo.colspan"
                      class="grade-cell grade-cell--head-col"
                      :class="{ 'grade-cell--head-sem': grupo.valor === '' }"
                    >
                      {{ grupo.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th class="grade-cell grade-cell--head-row">Quantidade</th>
                    <td
                      v-for="coluna in colunasExpandidas"
                      :key="coluna.chave"
                      class="grade-cell grade-cell--multi"
                      :class="{ 'grade-cell--vazia': !coluna.sku && !skusPara({ [eixoColuna.coluna]: coluna.valor }).length }"
                    >
                      <template v-if="coluna.sku">
                        <div class="sku-card" :title="coluna.sku.descComp">
                          <img
                            :src="cdnUrlSku(coluna.sku.produto_id)"
                            class="sku-card__imagem"
                            :alt="coluna.sku.descComp"
                            @error="$event.target.style.visibility = 'hidden'"
                          />
                          <div class="sku-card__id">#{{ coluna.sku.produto_id }}</div>
                          <div class="sku-card__descricao" :title="coluna.sku.descComp">{{ coluna.sku.descComp }}</div>
                          <input class="sku-card__quantidade" type="number" min="0" placeholder="0" />
                        </div>
                      </template>
                      <template v-else-if="skusPara({ [eixoColuna.coluna]: coluna.valor }).length">
                        <div
                          v-for="sku in skusPara({ [eixoColuna.coluna]: coluna.valor })"
                          :key="sku.produto_id"
                          class="sku-card"
                          :title="sku.descComp"
                        >
                          <img
                            :src="cdnUrlSku(sku.produto_id)"
                            class="sku-card__imagem"
                            :alt="sku.descComp"
                            @error="$event.target.style.visibility = 'hidden'"
                          />
                          <div class="sku-card__id">#{{ sku.produto_id }}</div>
                          <div class="sku-card__descricao" :title="sku.descComp">{{ sku.descComp }}</div>
                          <input class="sku-card__quantidade" type="number" min="0" placeholder="0" />
                        </div>
                      </template>
                      <span v-else class="grade-cell__vazio">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- MATRIZ 1D — só linha (1 coluna vertical) -->
            <div v-else-if="modo === 'so-linha'" class="grade-tabela-wrapper">
              <table class="grade-tabela">
                <thead>
                  <tr>
                    <th class="grade-cell grade-cell--head-row">
                      <span class="eixo-rotulo eixo-rotulo--linha">{{ eixoLinha.label }}</span>
                    </th>
                    <th class="grade-cell grade-cell--head-col">Quantidade</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(linha, indice) in linhasExpandidas" :key="linha.chave">
                    <th
                      v-if="infoCabecalhoLinha(indice).renderiza"
                      :rowspan="infoCabecalhoLinha(indice).rowspan"
                      class="grade-cell grade-cell--head-row"
                      :class="{ 'grade-cell--head-sem': infoCabecalhoLinha(indice).ehSem }"
                    >
                      {{ infoCabecalhoLinha(indice).label }}
                    </th>
                    <td
                      class="grade-cell grade-cell--multi"
                      :class="{ 'grade-cell--vazia': !linha.sku && !skusPara({ [eixoLinha.coluna]: linha.valor }).length }"
                    >
                      <template v-if="linha.sku">
                        <div class="sku-card" :title="linha.sku.descComp">
                          <img
                            :src="cdnUrlSku(linha.sku.produto_id)"
                            class="sku-card__imagem"
                            :alt="linha.sku.descComp"
                            @error="$event.target.style.visibility = 'hidden'"
                          />
                          <div class="sku-card__id">#{{ linha.sku.produto_id }}</div>
                          <div class="sku-card__descricao" :title="linha.sku.descComp">{{ linha.sku.descComp }}</div>
                          <input class="sku-card__quantidade" type="number" min="0" placeholder="0" />
                        </div>
                      </template>
                      <template v-else-if="skusPara({ [eixoLinha.coluna]: linha.valor }).length">
                        <div
                          v-for="sku in skusPara({ [eixoLinha.coluna]: linha.valor })"
                          :key="sku.produto_id"
                          class="sku-card"
                          :title="sku.descComp"
                        >
                          <img
                            :src="cdnUrlSku(sku.produto_id)"
                            class="sku-card__imagem"
                            :alt="sku.descComp"
                            @error="$event.target.style.visibility = 'hidden'"
                          />
                          <div class="sku-card__id">#{{ sku.produto_id }}</div>
                          <div class="sku-card__descricao" :title="sku.descComp">{{ sku.descComp }}</div>
                          <input class="sku-card__quantidade" type="number" min="0" placeholder="0" />
                        </div>
                      </template>
                      <span v-else class="grade-cell__vazio">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- LISTA — sem eixos -->
            <div v-else class="grade-tabela-wrapper">
              <table class="grade-tabela">
                <thead>
                  <tr>
                    <th class="grade-cell grade-cell--head-row">SKU</th>
                    <th
                      v-for="campo in todosCampos"
                      :key="campo.coluna"
                      class="grade-cell grade-cell--head-col"
                    >
                      {{ campo.label }}
                    </th>
                    <th class="grade-cell grade-cell--head-col">Quantidade</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sku in skusFiltrados" :key="sku.produto_id">
                    <th class="grade-cell grade-cell--head-row" :title="sku.descComp">
                      #{{ sku.produto_id }}
                    </th>
                    <td
                      v-for="campo in todosCampos"
                      :key="campo.coluna"
                      class="grade-cell grade-cell--valor"
                    >
                      {{ labelValor(campo.coluna, sku[campo.coluna]) || '—' }}
                    </td>
                    <td class="grade-cell">
                      <input class="sku-card__quantidade" type="number" min="0" placeholder="0" />
                    </td>
                  </tr>
                  <tr v-if="!skusFiltrados.length">
                    <td :colspan="todosCampos.length + 2" class="grade-cell grade-cell--vazia">
                      <span class="grade-cell__vazio">Nenhum SKU com esses filtros</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="grade-rodape">
              <CIcon name="cil-grid" size="sm" />
              <span>
                <strong>{{ skusFiltrados.length }}</strong> de {{ amostra.skus.length }} SKUs visíveis
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, post } from "@/helpers/apiRequest";
import { cdnUrlProduto } from "@/helpers/url";

export default {
  name: "StepPreview",
  props: {
    form: { type: Object, required: true },
    colunasDisponiveis: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      carregado: false,
      total: 0,
      produtos: [],
      amostra: null,
      selecionado: null,
      temMais: false,
      limiteAplicado: 20,
      excedeLimite: false,
      maximoCluster: 0,
      limitePermitido: 200,
      filtrosSelecionados: {},
      labelsPorValor: {}, // { coluna: { id: label } }
    };
  },
  computed: {
    podeCarregar() {
      return (
        (this.form.regras || []).length > 0 &&
        (this.form.campos || []).length > 0
      );
    },
    todosCampos() {
      if (!this.amostra) return [];
      return [...this.amostra.campos].sort(
        (campoA, campoB) => (campoA.ordem || 0) - (campoB.ordem || 0)
      );
    },
    eixoLinha() {
      return this.todosCampos.find((campo) => campo.eixo === "linha") || null;
    },
    eixoColuna() {
      return this.todosCampos.find((campo) => campo.eixo === "coluna") || null;
    },
    filtros() {
      return this.todosCampos.filter(
        (campo) => campo.eixo !== "linha" && campo.eixo !== "coluna"
      );
    },
    modo() {
      if (this.eixoLinha && this.eixoColuna) return "matriz2d";
      if (this.eixoColuna && !this.eixoLinha) return "so-coluna";
      if (this.eixoLinha && !this.eixoColuna) return "so-linha";
      return "lista";
    },
    skusFiltrados() {
      if (!this.amostra) return [];
      return this.amostra.skus.filter((sku) => {
        for (const [coluna, valor] of Object.entries(this.filtrosSelecionados)) {
          if (valor === null || valor === undefined) continue;
          if (String(sku[coluna] ?? "") !== String(valor)) return false;
        }
        return true;
      });
    },
    /**
     * Expande os valores do eixo coluna: valores preenchidos viram 1 célula,
     * e SKUs sem variação ganham 1 célula individual cada (no body).
     */
    colunasExpandidas() {
      if (!this.eixoColuna) return [];
      return this.expandirValoresEixo(this.eixoColuna);
    },
    linhasExpandidas() {
      if (!this.eixoLinha) return [];
      return this.expandirValoresEixo(this.eixoLinha);
    },
    /**
     * Cabeçalhos agrupados: SKUs consecutivos "sem variação" colapsam em 1 th
     * com colspan, com label "Sem {nome do eixo}".
     */
    gruposCabecalhoColuna() {
      return this.agruparCabecalho(this.colunasExpandidas, this.eixoColuna);
    },
    gruposCabecalhoLinha() {
      return this.agruparCabecalho(this.linhasExpandidas, this.eixoLinha);
    },
  },
  watch: {
    amostra() {
      this.recarregarFiltros();
      this.preCarregarLabels();
    },
  },
  methods: {
    carregar() {
      if (!this.podeCarregar) return;
      this.loading = true;
      post("/admin/grupo-grade/preview", {
        regras: this.form.regras,
        campos: this.form.campos,
        limite: 20,
      })
        .then((json) => {
          const dados = json.data || json;
          this.excedeLimite = !!dados.excede_limite;
          this.maximoCluster = dados.maximo_cluster || 0;
          this.limitePermitido = dados.limite_permitido || 200;
          this.total = dados.total;
          this.produtos = dados.produtos || [];
          this.amostra = dados.amostra || null;
          this.temMais = !!dados.has_more;
          this.limiteAplicado = dados.limite_aplicado || 20;
          this.selecionado = dados.amostra ? dados.amostra.produto_principal_id : null;
          this.carregado = true;
        })
        .catch((erro) => {
          this.$swal({
            title: "Falha ao gerar preview",
            text: erro.message || "Tente novamente.",
            icon: "error",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    selecionar(produtoId) {
      if (produtoId === this.selecionado) return;
      this.loading = true;
      post("/admin/grupo-grade/preview", {
        regras: this.form.regras,
        campos: this.form.campos,
        limite: 1,
        produtoReferenciaId: produtoId,
      })
        .then((json) => {
          const dados = json.data || json;
          this.amostra = dados.amostra || null;
          this.selecionado = produtoId;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    recarregarFiltros() {
      const novosFiltros = {};
      (this.filtros || []).forEach((filtro) => {
        novosFiltros[filtro.coluna] = null;
      });
      this.filtrosSelecionados = novosFiltros;
    },
    skusPara(criterios) {
      return this.skusFiltrados.filter((sku) =>
        Object.entries(criterios).every(
          ([coluna, valor]) => String(sku[coluna] ?? "") === String(valor)
        )
      );
    },
    /**
     * Para um eixo, gera lista de células do body [{ valor, sku?, chave }].
     * Quando o valor é "" (sem variação), expande um item por SKU sem aquela coluna.
     */
    expandirValoresEixo(eixo) {
      const itens = [];
      (eixo.valores || []).forEach((valor) => {
        if (valor === "" || valor === null || valor === undefined) {
          const skusSemValor = this.skusFiltrados.filter(
            (sku) => sku[eixo.coluna] === null || sku[eixo.coluna] === "" || sku[eixo.coluna] === undefined
          );
          skusSemValor.forEach((sku) => {
            itens.push({
              valor: "",
              sku,
              chave: `sem-variacao-${sku.produto_id}`,
            });
          });
        } else {
          itens.push({
            valor,
            sku: null,
            chave: String(valor),
          });
        }
      });
      return itens;
    },
    /**
     * Agrupa células consecutivas com mesmo valor em 1 cabeçalho com colspan.
     * Para os SKUs sem variação, todos colapsam em "Sem {label do eixo}".
     */
    agruparCabecalho(celulas, eixo) {
      if (!eixo) return [];
      const grupos = [];
      let atual = null;
      celulas.forEach((celula) => {
        const ehSemVariacao = celula.valor === "" || celula.valor === null || celula.valor === undefined;
        if (ehSemVariacao && atual && atual.valor === "") {
          atual.colspan += 1;
          return;
        }
        atual = {
          valor: ehSemVariacao ? "" : celula.valor,
          label: ehSemVariacao
            ? this.labelSemVariacao(eixo)
            : this.labelValor(eixo.coluna, celula.valor),
          colspan: 1,
          chave: ehSemVariacao ? "grupo-sem-variacao" : `grupo-${celula.valor}`,
        };
        grupos.push(atual);
      });
      return grupos;
    },
    cdnUrlSku(produtoId) {
      return cdnUrlProduto(produtoId);
    },
    labelSemVariacao(eixo) {
      const labelEixo = (eixo && eixo.label) ? String(eixo.label).trim() : "";
      if (!labelEixo) return "Sem variação";
      const semPrefixo = labelEixo.replace(/^Dimensão \d+\s*→\s*/i, "");
      return `Sem ${semPrefixo.toLowerCase()}`;
    },
    /**
     * Para o modo so-linha: dado um índice em linhasExpandidas, retorna o info
     * do <th> que abre o grupo (com rowspan), ou { renderiza: false } se a célula
     * é continuação de um grupo "sem variação" anterior.
     */
    infoCabecalhoLinha(indice) {
      const linhas = this.linhasExpandidas;
      const linha = linhas[indice];
      const ehSem = linha.valor === "" || linha.valor === null || linha.valor === undefined;
      const anterior = linhas[indice - 1];
      const anteriorEhSem = anterior && (anterior.valor === "" || anterior.valor === null || anterior.valor === undefined);

      if (ehSem && anteriorEhSem) {
        return { renderiza: false };
      }

      let rowspan = 1;
      if (ehSem) {
        for (let proximo = indice + 1; proximo < linhas.length; proximo++) {
          const proximaLinha = linhas[proximo];
          const proximaEhSem = proximaLinha.valor === "" || proximaLinha.valor === null || proximaLinha.valor === undefined;
          if (!proximaEhSem) break;
          rowspan++;
        }
      }

      return {
        renderiza: true,
        rowspan,
        ehSem,
        label: ehSem
          ? this.labelSemVariacao(this.eixoLinha)
          : this.labelValor(this.eixoLinha.coluna, linha.valor),
      };
    },
    labelValor(coluna, valor) {
      if (valor === null || valor === undefined || valor === "") {
        return "(sem variação)";
      }
      const mapaLabels = this.labelsPorValor[coluna];
      const labelCadastrado = mapaLabels ? mapaLabels[String(valor)] : null;
      if (labelCadastrado && labelCadastrado !== String(valor)) {
        return `${labelCadastrado} (${valor})`;
      }
      return String(valor);
    },
    preCarregarLabels() {
      if (!this.amostra) return;
      const colunasParaCarregar = this.todosCampos
        .map((campo) => campo.coluna)
        .filter((coluna) => !this.labelsPorValor[coluna]);

      colunasParaCarregar.forEach((coluna) => {
        this.$set(this.labelsPorValor, coluna, {});
        get(`/admin/grupo-grade/valores-coluna?coluna=${encodeURIComponent(coluna)}`)
          .then((json) => {
            const valores = json.data || json || [];
            const mapaLabels = {};
            valores.forEach((valor) => {
              mapaLabels[String(valor.id)] = valor.label;
            });
            this.$set(this.labelsPorValor, coluna, mapaLabels);
          })
          .catch(() => {});
      });
    },
  },
};
</script>

<style scoped>
.step-preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-preview__hint {
  font-size: 13px;
  color: #475467;
  padding: 10px 12px;
  background: #f9fafb;
  border-left: 3px solid #2298a3;
  border-radius: 4px;
}

.step-preview__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-preview__hint-secondary {
  font-size: 12px;
  color: #98a2b3;
}

.step-preview__resultado {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-preview__total {
  font-size: 14px;
  color: #344054;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
}

.step-preview__alerta {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: #fef3f2;
  border: 1px solid #fcdcd7;
  border-left: 4px solid #d92d20;
  border-radius: 8px;
  color: #912018;
  font-size: 13px;
}

.step-preview__alerta strong {
  display: block;
  font-size: 14px;
  margin-bottom: 4px;
  color: #7a1b13;
}

.step-preview__limite {
  color: #98a2b3;
  font-size: 12px;
}

.step-preview__empty {
  text-align: center;
  padding: 32px 20px;
  color: #98a2b3;
  background: #f9fafb;
  border: 1px dashed #dddce9;
  border-radius: 10px;
}

.step-preview__empty--small {
  padding: 16px;
  font-size: 12px;
}

.step-preview__empty .empty-icon {
  color: #d0d5dd;
  margin-bottom: 8px;
}

.step-preview__empty p {
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 4px;
}

.step-preview__grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
}

.step-preview__lista,
.step-preview__grade {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.step-preview__lista-titulo,
.step-preview__grade-titulo {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 10px 12px;
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
}

.step-preview__grade-sublabel {
  margin-top: 4px;
  font-size: 11px;
  font-weight: 400;
  color: #98a2b3;
  text-transform: none;
  letter-spacing: 0;
  font-style: italic;
}

.step-preview__lista {
  max-height: 420px;
  overflow-y: auto;
}

.step-preview__item {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #f2f4f7;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
  transition: background 0.1s;
}

.step-preview__item:last-child {
  border-bottom: none;
}

.step-preview__item:hover {
  background: #f9fafb;
}

.step-preview__item--ativo {
  background: #f2f2f8;
  color: #3a3e81;
  font-weight: 500;
}

.step-preview__item-id {
  color: #98a2b3;
  font-weight: 500;
  min-width: 40px;
}

.step-preview__item-descricao {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ======= GRADE PREVIEW ======= */
.step-preview__grade-corpo {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.grade-filtros {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
}

.grade-filtro {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;
}

.grade-filtro label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 0;
}

.grade-filtro .form-control {
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
  padding: 6px 10px;
}

.grade-filtro .form-control:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.grade-tabela-wrapper {
  overflow-x: auto;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
}

.grade-tabela {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  font-size: 12px;
}

.grade-cell {
  padding: 6px 8px;
  border: 1px solid #f2f4f7;
  text-align: center;
  vertical-align: middle;
  min-width: 80px;
}

.grade-cell--corner {
  background: linear-gradient(135deg, rgba(58, 62, 129, 0.08), rgba(34, 152, 163, 0.08));
  text-align: left;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 11px;
  white-space: nowrap;
}

.grade-cell--head-col {
  background: #f9fafb;
  color: #2298a3;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.grade-cell--head-row {
  background: #f9fafb;
  color: #3a3e81;
  font-weight: 600;
  text-align: left;
  padding: 8px 12px;
  font-size: 12px;
  white-space: nowrap;
}

.grade-cell--head-sem {
  background: #fafafb;
  color: #98a2b3;
  font-style: italic;
}

.grade-cell--valor {
  color: #475467;
  font-size: 12px;
  text-align: left;
}

.grade-cell--multi {
  padding: 6px;
  vertical-align: top;
}

.grade-cell--multi .sku-card + .sku-card {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #e4e7eb;
}

.grade-cell--vazia {
  background: repeating-linear-gradient(
    45deg,
    #fafafa,
    #fafafa 6px,
    #f5f5f7 6px,
    #f5f5f7 12px
  );
}

.grade-cell__vazio {
  color: #cbd5e1;
  font-size: 14px;
}

.eixo-rotulo {
  display: inline;
  font-weight: 700;
}

.eixo-rotulo--linha {
  color: #3a3e81;
}

.eixo-rotulo--coluna {
  color: #2298a3;
}

.eixo-rotulo-sep {
  margin: 0 4px;
  color: #98a2b3;
  font-weight: 400;
}

/* SKU card dentro da célula */
.sku-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 4px;
  min-width: 90px;
}

.sku-card__imagem {
  width: 56px;
  height: 56px;
  object-fit: contain;
  background: #fafafb;
  border: 1px solid #f2f4f7;
  border-radius: 6px;
}

.sku-card__id {
  font-size: 10px;
  color: #98a2b3;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.sku-card__descricao {
  font-size: 11px;
  color: #475467;
  line-height: 1.25;
  text-align: center;
  max-width: 110px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sku-card__quantidade {
  width: 60px;
  height: 26px;
  border: 1px solid #dddce9;
  border-radius: 4px;
  text-align: center;
  font-size: 12px;
  padding: 2px;
  background: #fff;
}

.sku-card__quantidade:focus {
  outline: none;
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.grade-rodape {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 6px;
  font-size: 12px;
  color: #1a7a83;
}
</style>
